/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {BlocklistApiService, BlocklistModel} from './blocklist-api.service';
import {BlocklistEditPageComponent} from './blocklist-edit-page.component';
import {BlocklistRoutePageComponent} from './blocklist-route-page.component';

export function blocklistStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(BlocklistApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasAny(PERMISSIONS.managePublishing.apiKey)
    && !permissions.hasAny(PERMISSIONS.axPublisher.apiKey);
  const rh = adnRouterHelper.createRouterHelper('blocklists.blocklist', BlocklistApiService, 'app.inv', 'app');

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: BlocklistRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      blocklistPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: BlocklistModel = {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []};
          return new SignalAdminResourcePageState(model, true, readOnly(), api, 'Blocklist');
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Blocklist'));
      },
      model: function(blocklistPage: SignalAdminResourcePageState<BlocklistModel>) {
        return blocklistPage.model();
      }
    },
    views: {
      'content@app': {component: BlocklistEditPageComponent, bindings: {state: 'blocklistPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'blocklistPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'blocklistPage'}}
    }
  }));

  return states.build();
}
